import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import '../styles/CourseOfStudyTracker.css';

const CourseOfStudyTracker = ({ candidateId, candidate }) => {
  const toast = useToast();
  const confirm = useConfirm();
  const [courses, setCourses] = useState([]);
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [newCourse, setNewCourse] = useState({
    courseCode: '',
    courseName: '',
    grade: '',
    semester: '',
    year: '',
    instructor: '',
    status: 'in-progress'
  });

  // Official Course of Study classes
  const availableCourses = [
    { code: 'COS 121', name: 'Bible I: Introduction' },
    { code: 'COS 122', name: 'Theological Heritage I: Introduction' },
    { code: 'COS 123', name: 'Formation & Discipleship' },
    { code: 'COS 124', name: 'Transformative Leadership' },
    { code: 'COS 221', name: 'Bible II: Torah & Israel\'s History' },
    { code: 'COS 222', name: 'Theological Heritage II: Early Church' },
    { code: 'COS 223', name: 'Worship & Sacraments' },
    { code: 'COS 224', name: 'Administration & Polity' },
    { code: 'COS 321', name: 'Bible III: Gospels' },
    { code: 'COS 322', name: 'Theological Heritage III: Medieval-Reformation' },
    { code: 'COS 323', name: 'Congregational Care' },
    { code: 'COS 324', name: 'Preaching' },
    { code: 'COS 421', name: 'Bible IV: Prophets, Psalms & Wisdom Lit' },
    { code: 'COS 422', name: 'Theological Heritage IV: Wesleyan Movement' },
    { code: 'COS 423', name: 'Mission' },
    { code: 'COS 424', name: 'Ethics' },
    { code: 'COS 521', name: 'Bible V: Acts, Epistles & Revelation' },
    { code: 'COS 522', name: 'Theology in the Contemporary Church' },
    { code: 'COS 523', name: 'Evangelism' },
    { code: 'COS 524', name: 'Theological Reflection: Practice of Ministry' }
  ];

  useEffect(() => {
    if (candidate?.courseOfStudy?.courses) {
      setCourses(candidate.courseOfStudy.courses);
    }
  }, [candidate]);

  const handleCourseSelect = (selectedCourse) => {
    setNewCourse({
      ...newCourse,
      courseCode: selectedCourse.code,
      courseName: selectedCourse.name
    });
  };

  const handleAddCourse = async () => {
    if (!newCourse.courseCode || !newCourse.courseName) {
      toast.warning('Please select a course or enter course details.');
      return;
    }

    try {
      const updatedCourses = [...courses, { ...newCourse, id: Date.now().toString() }];
      const completedCourses = updatedCourses.filter(course => course.status === 'completed').length;

      await updateDoc(doc(db, 'candidates', candidateId), {
        courseOfStudy: { courses: updatedCourses, completedCourses, totalCourses: updatedCourses.length },
        updatedAt: new Date()
      });

      setCourses(updatedCourses);
      setNewCourse({ courseCode: '', courseName: '', grade: '', semester: '', year: '', instructor: '', status: 'in-progress' });
      setShowAddCourse(false);
      toast.success('Course added.');
    } catch (error) {
      console.error('Error adding course:', error);
      toast.error('Error adding course. Please try again.');
    }
  };

  const handleUpdateCourse = async (courseId, updatedData) => {
    try {
      const updatedCourses = courses.map(course =>
        course.id === courseId ? { ...course, ...updatedData } : course
      );
      const completedCourses = updatedCourses.filter(course => course.status === 'completed').length;

      await updateDoc(doc(db, 'candidates', candidateId), {
        courseOfStudy: { courses: updatedCourses, completedCourses, totalCourses: updatedCourses.length },
        updatedAt: new Date()
      });

      setCourses(updatedCourses);
    } catch (error) {
      console.error('Error updating course:', error);
      toast.error('Error updating course. Please try again.');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    const confirmed = await confirm({
      title: 'Delete Course',
      message: 'Are you sure you want to delete this course record?',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      const updatedCourses = courses.filter(course => course.id !== courseId);
      const completedCourses = updatedCourses.filter(course => course.status === 'completed').length;

      await updateDoc(doc(db, 'candidates', candidateId), {
        courseOfStudy: { courses: updatedCourses, completedCourses, totalCourses: updatedCourses.length },
        updatedAt: new Date()
      });

      setCourses(updatedCourses);
      toast.success('Course deleted.');
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Error deleting course. Please try again.');
    }
  };

  const getGradeColor = (grade) => {
    if (!grade) return '';
    const gradeUpper = grade.toUpperCase();
    if (gradeUpper === 'A' || gradeUpper === 'A+') return 'grade-a';
    if (gradeUpper === 'B' || gradeUpper === 'B+') return 'grade-b';
    if (gradeUpper === 'C' || gradeUpper === 'C+') return 'grade-c';
    if (gradeUpper === 'D' || gradeUpper === 'F') return 'grade-d';
    return '';
  };

  const completedCourses = courses.filter(course => course.status === 'completed').length;
  const attemptedCourses = courses.filter(course => 
    course.status === 'completed' || course.status === 'in-progress' || course.status === 'dropped'
  ).length;
  const totalRequiredCourses = 20;
  const progressPercentage = (completedCourses / totalRequiredCourses) * 100;

  return (
    <div className="course-of-study-tracker">
      <div className="tracker-header">
        <h2>Course of Study Progress</h2>
        <div className="progress-summary">
          <div className="course-summary">
            <div className="summary-item">
              <span className="completed-courses">{completedCourses}</span>
              <span className="separator">/</span>
              <span className="total-courses">{totalRequiredCourses}</span>
              <span className="courses-label">Completed</span>
            </div>
            <div className="summary-item">
              <span className="attempted-courses">{attemptedCourses}</span>
              <span className="courses-label">Attempted</span>
            </div>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>
        <button 
          onClick={() => setShowAddCourse(true)}
          className="add-course-btn"
        >
          Add Course
        </button>
      </div>

      {showAddCourse && (
        <div className="add-course-modal">
          <div className="modal-content">
            <h3>Add New Course</h3>
            <div className="course-form">
              <div className="course-selection">
                <h4>Select from Official Course of Study:</h4>
                <div className="available-courses">
                  {availableCourses.map(course => (
                    <button
                      key={course.code}
                      onClick={() => handleCourseSelect(course)}
                      className={`course-option ${newCourse.courseCode === course.code ? 'selected' : ''}`}
                    >
                      <strong>{course.code}</strong>
                      <span>{course.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="manual-entry">
                <h4>Or Enter Manually:</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Course Code</label>
                    <input
                      type="text"
                      value={newCourse.courseCode}
                      onChange={(e) => setNewCourse({...newCourse, courseCode: e.target.value})}
                      placeholder="e.g., COS 121"
                    />
                  </div>
                  <div className="form-group">
                    <label>Course Name</label>
                    <input
                      type="text"
                      value={newCourse.courseName}
                      onChange={(e) => setNewCourse({...newCourse, courseName: e.target.value})}
                      placeholder="e.g., Bible I: Introduction"
                    />
                  </div>
                </div>
              </div>

              <div className="course-details">
                <div className="form-row">
                  <div className="form-group">
                    <label>Grade</label>
                    <input
                      type="text"
                      value={newCourse.grade}
                      onChange={(e) => setNewCourse({...newCourse, grade: e.target.value})}
                      placeholder="e.g., A, B+, C"
                    />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={newCourse.status}
                      onChange={(e) => setNewCourse({...newCourse, status: e.target.value})}
                    >
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="planned">Planned</option>
                      <option value="dropped">Dropped</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Semester</label>
                    <select
                      value={newCourse.semester}
                      onChange={(e) => setNewCourse({...newCourse, semester: e.target.value})}
                    >
                      <option value="">Select Semester</option>
                      <option value="Spring">Spring</option>
                      <option value="Summer">Summer</option>
                      <option value="Fall">Fall</option>
                      <option value="Winter">Winter</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Year</label>
                    <input
                      type="number"
                      value={newCourse.year}
                      onChange={(e) => setNewCourse({...newCourse, year: e.target.value})}
                      min="2000"
                      max="2030"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Instructor</label>
                  <input
                    type="text"
                    value={newCourse.instructor}
                    onChange={(e) => setNewCourse({...newCourse, instructor: e.target.value})}
                    placeholder="Instructor name"
                  />
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowAddCourse(false)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleAddCourse} className="save-btn">
                Add Course
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="courses-list">
        {courses.length === 0 ? (
          <div className="no-courses">
            <p>No courses added yet. Click "Add Course" to get started.</p>
          </div>
        ) : (
          courses
            .sort((a, b) => {
              // Extract course numbers from course codes (e.g., "COS 121" -> 121)
              const getNumber = (courseCode) => {
                const match = courseCode.match(/\d+/);
                return match ? parseInt(match[0]) : 0;
              };
              return getNumber(a.courseCode) - getNumber(b.courseCode);
            })
            .map(course => (
              <CourseCard
                key={course.id}
                course={course}
                onUpdate={(updatedData) => handleUpdateCourse(course.id, updatedData)}
                onDelete={() => handleDeleteCourse(course.id)}
                getGradeColor={getGradeColor}
              />
            ))
        )}
      </div>
    </div>
  );
};

const CourseCard = ({ course, onUpdate, onDelete, getGradeColor }) => {
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState(course);

  const handleSave = () => {
    onUpdate(editData);
    setEditing(false);
  };

  const handleCancel = () => {
    setEditData(course);
    setEditing(false);
  };

  return (
    <div className={`course-card ${course.status}`}>
      <div className="course-header">
        <div className="course-title">
          {editing ? (
            <div className="edit-title">
              <input
                type="text"
                value={editData.courseCode}
                onChange={(e) => setEditData({...editData, courseCode: e.target.value})}
                className="course-code-input"
              />
              <input
                type="text"
                value={editData.courseName}
                onChange={(e) => setEditData({...editData, courseName: e.target.value})}
                className="course-name-input"
              />
            </div>
          ) : (
            <>
              <h4>{course.courseCode}</h4>
              <p>{course.courseName}</p>
            </>
          )}
        </div>
        <div className="course-actions">
          {editing ? (
            <>
              <button onClick={handleSave} className="save-btn">Save</button>
              <button onClick={handleCancel} className="cancel-btn">Cancel</button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(true)} className="edit-btn">Edit</button>
              <button onClick={onDelete} className="delete-btn">Delete</button>
            </>
          )}
        </div>
      </div>

      <div className="course-details">
        <div className="course-info">
          <div className="info-item">
            <label>Grade:</label>
            {editing ? (
              <input
                type="text"
                value={editData.grade}
                onChange={(e) => setEditData({...editData, grade: e.target.value})}
              />
            ) : (
              <span className={`grade ${getGradeColor(course.grade)}`}>
                {course.grade || 'Not graded'}
              </span>
            )}
          </div>
          <div className="info-item">
            <label>Semester:</label>
            {editing ? (
              <div className="semester-edit">
                <select
                  value={editData.semester}
                  onChange={(e) => setEditData({...editData, semester: e.target.value})}
                >
                  <option value="">Select Semester</option>
                  <option value="Spring">Spring</option>
                  <option value="Summer">Summer</option>
                  <option value="Fall">Fall</option>
                  <option value="Winter">Winter</option>
                </select>
                <input
                  type="number"
                  value={editData.year}
                  onChange={(e) => setEditData({...editData, year: e.target.value})}
                  min="2000"
                  max="2030"
                  placeholder="Year"
                />
              </div>
            ) : (
              <span>{course.semester} {course.year}</span>
            )}
          </div>
          <div className="info-item">
            <label>Instructor:</label>
            {editing ? (
              <input
                type="text"
                value={editData.instructor}
                onChange={(e) => setEditData({...editData, instructor: e.target.value})}
              />
            ) : (
              <span>{course.instructor || 'Not specified'}</span>
            )}
          </div>
          <div className="info-item">
            <label>Status:</label>
            {editing ? (
              <select
                value={editData.status}
                onChange={(e) => setEditData({...editData, status: e.target.value})}
              >
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="planned">Planned</option>
                <option value="dropped">Dropped</option>
              </select>
            ) : (
              <span className={`status ${course.status}`}>
                {course.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseOfStudyTracker;
